import { useToggle } from '@mantine/hooks'
import Decimal from 'decimal.js'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import { useStockContext } from '@/app/[locale]/price/stocks/[symbol]/_contexts/StockContext'
import { getCurrencyInfo } from '@/configs/currencies'
import { useSsrContext } from '@/contexts/ssr'
import { useCurrencyAmounts } from '@/hooks/business/useCurrencyAmounts'
import { currencyFormatter } from '@/utils/number'
import { allTokenQuantitiesFormat } from '@/utils/number/format'

export function usePriceConvert() {
  const [currencyValue, setCurrencyValue] = useState<number | string>(10)
  const { stock } = useStockContext()

  const t = useTranslations('tradeWidget')

  /**
   * default value is false
   */
  const [reversed, toggleReversed] = useToggle()

  const { selectedCurrency } = useSsrContext()
  const {
    amounts: [{ value: coinCurrencyPrice }],
  } = useCurrencyAmounts({
    amounts: [stock.usd_price],
    currency: getCurrencyInfo(selectedCurrency),
    tokenSymbol: stock.symbol,
    isUsedForTokenPrice: true,
    isExchangeRateBasedOnSymbolCurrency: false,
  })
  const scale = coinCurrencyPrice ?? 1

  const coinQuantity = useMemo(() => {
    if (currencyValue === '') return ''
    return new Decimal(currencyValue).div(scale).toString()
  }, [scale, currencyValue])

  const toValue = useMemo(() => {
    if (currencyValue === '') return null
    if (reversed) {
      const { value } = currencyFormatter('', Number(currencyValue))
      return value
    }
    return allTokenQuantitiesFormat(coinQuantity)
  }, [reversed, currencyValue, coinQuantity])

  const fromValue = useMemo(() => {
    if (currencyValue === '') return ''
    if (!reversed) return currencyValue
    return coinQuantity
  }, [reversed, currencyValue, coinQuantity])

  const toUnit = Number(String(toValue).replace(/,/g, '')) > 1 ? t('share_plural') : t('share')
  const fromUnit = Number(String(fromValue).replace(/,/g, '')) > 1 ? t('share_plural') : t('share')
  const onInputValue = (value: number | string) => {
    if (value === '') {
      setCurrencyValue('')
      return
    }
    if (reversed) {
      setCurrencyValue(new Decimal(value).mul(scale).toString())
    } else {
      setCurrencyValue(value)
    }
  }

  const fromSymbol = useMemo(() => {
    if (reversed) return fromUnit
    return selectedCurrency
  }, [reversed, fromUnit, selectedCurrency])

  const toSymbol = useMemo(() => {
    if (reversed) return selectedCurrency
    return toUnit
  }, [reversed, toUnit, selectedCurrency])

  return {
    fromValue,
    toValue,
    fromSymbol,
    toSymbol,
    onInputValue,
    toggleReversed,
    setCurrencyValue,
  }
}
